import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { isTesting } from '@embroider/macros';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import type AnalyticsService from '@ebiz/ember-aditools-analytics/services/aditools-analytics';
import type IntlService from 'ember-intl/services/intl';

import type LoggingService from '../services/logger';
import type CdpService from '../services/cdp';
import type OpampService from '../services/opamp';
import type SensorService from '../services/sensor';
import type ModelService from '../services/model';
import { loadTimeAnalytics } from '../utils/analytics';
import ApplicationController from '../controllers/application';
import ENV from '../config/environment';
import '@ebiz/ember-aditools-graph/ember-aditools-graph.css';

type Transition = ReturnType<RouterService['transitionTo']>;

const LOCALES: { [name: string]: string } = { 'en-us': 'en', 'zh-cn': 'cn', 'ja-jp': 'jp', 'ru-ru': 'ru' };

// let lookForAttributeChanges = function(element?: HTMLElement) {
//         // Had to find out what was rewriting the lang attribute to 'en-us', so I wrote the following snippet of code.
//         // It turns out it's updateDocumentLanguage() in ember-intl, when you set the locale.
//         if (element) {
//             let observer = new MutationObserver(function(mutations) {
//                 mutations.forEach(function(mutation) {
//                   if (mutation.type == "attributes") {
//                     console.log("attributes changed")
//                   }
//                 });
//             });
//             observer.observe(element, {
//                 attributes: true //configure it to listen to attribute changes
//             });
//         }
// }
export default class ApplicationRoute extends Route {
  @service declare intl: IntlService;
  @service declare logger: LoggingService;
  @service('aditools-site') declare siteconfig: SiteService;
  @service('aditools-analytics') declare analytics: AnalyticsService;
  @service('model') declare modelService: ModelService;
  @service declare cdp: CdpService;
  @service declare opamp: OpampService;
  @service declare sensor: SensorService;
  toolsConfig?: Promise<PhotodiodeConfigJson | null>;

  lang: string | null = null;
  pdf: string | null = null;

  async beforeModel(): Promise<void> {
    this.lang = new URLSearchParams(window.location.search).get('lang');
    this.pdf = new URLSearchParams(window.location.search).get('pdf');
    let htmlElement = document?.querySelector('html');
    //lookForAttributeChanges(htmlElement);
    let fullLanguage = htmlElement?.getAttribute('lang');
    if (!fullLanguage) fullLanguage = 'en-us';
    const defaultLanguage = this.lang ? this.lang : (LOCALES[fullLanguage] as string);
    const configURL = ENV.rootURL.endsWith('/') ? ENV.rootURL + 'siteconfig.json' : ENV.rootURL + '/siteconfig.json';
    await this.siteconfig.start(
      configURL,
      ENV.rootURL,
      defaultLanguage,
      this.intl.locales.concat('ru-ru'),
      this.logger,
    );
    if (this.siteconfig.FullLocale === 'ru-ru') {
      this.intl.setLocale('en-us');
    } else {
      this.intl.setLocale(this.siteconfig.FullLocale);
    }
    this.analytics.start(
      ENV.toolname,
      this.siteconfig['AnalyticsUrl'] as string,
      false,
      isTesting(),
      ENV.environment === 'development',
      this.logger,
    );
    this.toolsConfig = this.setupToolsConfig();
    this.opamp.load(this.toolsConfig);
    this.sensor.load(this.toolsConfig);
  }

  afterModel(): void {
    if (document.documentElement) {
      document.documentElement.setAttribute('lang', this.siteconfig.FullLocale);
    }
  }

  setupController(controller: ApplicationController, model: ModelService, transition: Transition): void {
    super.setupController(controller, model, transition);
    controller.lang = this.lang;
    controller.pdf = this.pdf;
    loadTimeAnalytics(window.appStartDateTime, new Date().getTime(), this.analytics); // Analytics Placed here for Load Complete. (Unless there is a better place)
  }

  setupToolsConfig(): Promise<PhotodiodeConfigJson | null> {
    return this.cdp.getToolsConfig<PhotodiodeConfigJson>('pd').then((config) => {
      return config;
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'aditools-site': SiteService;
    'aditools-analytics': AnalyticsService;
  }
}
